import '../styles/RunMonitorTaskPanel.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useProjectCollection, projectData } from '../lib/projectData';

const TOTAL_STEPS = 10;
const TICK_MS = 900;

const STATE_LABEL = {
  loading: 'Loading',
  empty: 'No run',
  idle: 'Ready',
  running: 'Running',
  complete: 'Complete',
  error: 'Failed',
};

function pad(n) { return String(n).padStart(2, '0'); }

function stamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function StateBlock({ phase, note }) {
  const label = STATE_LABEL[phase] || 'Ready';
  return (
    <div className={`rtp-state rtp-state--${phase}`} aria-live="polite">
      <p className="rtp-state__label">{label}</p>
      {phase === 'error' ? (
        <span className="rtp-state__dot"><i aria-hidden="true" />Halted</span>
      ) : phase === 'empty' ? null : (
        <span className="rtp-state__dot"><i aria-hidden="true" />{phase === 'running' ? 'Live' : phase === 'complete' ? 'Done' : 'Standby'}</span>
      )}
      {note ? <p className="rtp-state__note">{note}</p> : null}
    </div>
  );
}

function SteppedProgress({ step, running }) {
  const pct = Math.round((step / TOTAL_STEPS) * 100);
  return (
    <div className="rtp-progress">
      <div className="rtp-progress__top">
        <h3 className="rtp-progress__label">Credential test progress</h3>
        <p className="rtp-progress__pct" aria-live="polite">
          {pct}%<small>step {step}/{TOTAL_STEPS}</small>
        </p>
      </div>
      <ol className="rtp-segs" aria-label={`${step} of ${TOTAL_STEPS} steps complete`}>
        {Array.from({ length: TOTAL_STEPS }, (_, i) => {
          const n = i + 1;
          const on = n <= step;
          const current = running && n === step;
          return (
            <li
              key={`seg-${n}`}
              className={`rtp-seg${on ? ' rtp-seg--on' : ''}${current ? ' rtp-seg--current' : ''}`}
              aria-hidden="true"
            />
          );
        })}
      </ol>
      <p className="rtp-progress__last">
        {running ? `Working step ${step} of ${TOTAL_STEPS} · processing locally` : step === 0 ? 'Not started' : `Last step reached: ${step} of ${TOTAL_STEPS}`}
      </p>
    </div>
  );
}

function Skeletons() {
  return (
    <div className="rtp-progress" aria-hidden="true">
      <span className="rtp-skel rtp-skel--head" />
      <span className="rtp-skel" />
      <span className="rtp-skel" />
      <span className="rtp-skel rtp-skel--short" />
    </div>
  );
}

export default function RunMonitorTaskPanel() {
  const { data, loading, error, refresh } = useProjectCollection('test_run');
  const { data: errorLogData } = useProjectCollection('error_log_entry');
  const initialRunId = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('id') || '';
  }, []);

  const runs = Array.isArray(data) ? data : [];
  const [selectedRunId, setSelectedRunId] = useState(initialRunId);
  const [phase, setPhase] = useState('idle');
  const [step, setStep] = useState(0);
  const [lastLog, setLastLog] = useState('');
  const [errorRecord, setErrorRecord] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const runIdRef = useRef(null);

  const selectedRun = useMemo(() => {
    if (!runs.length) return null;
    return runs.find((r) => r.id === selectedRunId) || runs[0];
  }, [runs, selectedRunId]);

  // pick up a run once data arrives
  useEffect(() => {
    if (selectedRun && selectedRun.id !== selectedRunId) setSelectedRunId(selectedRun.id);
  }, [selectedRun, selectedRunId]);

  // hydrate lifecycle from the persisted record when the selection changes
  useEffect(() => {
    if (!selectedRun) return;
    const st = String(selectedRun.state || '').toLowerCase();
    if (runIdRef.current === selectedRun.id) return;
    runIdRef.current = selectedRun.id;
    setActionError('');
    if (st === 'running') {
      setPhase('running');
      setStep(Math.max(1, Math.round(TOTAL_STEPS * 0.6)));
    } else if (st === 'complete') {
      setPhase('complete');
      setStep(TOTAL_STEPS);
    } else if (st === 'failed' || st === 'error') {
      setPhase('error');
      setStep(Math.max(1, Math.round(TOTAL_STEPS * 0.6)));
    } else {
      setPhase('idle');
      setStep(0);
    }
  }, [selectedRun]);

  // stepped progress: one discrete segment per tick, persisted via the shared client
  useEffect(() => {
    if (phase === 'running') setLastLog('engine · step ' + step + ' of ' + TOTAL_STEPS + ' · segments advancing');
  }, [phase, step]);

  useEffect(() => {
    if (phase !== 'running' || !selectedRun) return undefined;
    const timer = setInterval(async () => {
      const run = selectedRun;
      const next = Math.min(TOTAL_STEPS, step + 1);
      setStep(next);
      try {
        if (next >= TOTAL_STEPS) {
          await projectData.update('test_run', run.id, { state: 'complete', updatedAt: new Date().toISOString() });
          setPhase('complete');
          setActionError('');
        } else if (step === 0 || next % 3 === 0) {
          await projectData.update('test_run', run.id, { state: 'running', updatedAt: new Date().toISOString() });
        }
      } catch (err) {
        setPhase('error');
        setErrorRecord({
          message: (err && err.message) || 'The local processing engine stopped responding.',
          step: `persist run state at step ${next} of ${TOTAL_STEPS}`,
        });
      }
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [phase, step, selectedRun]);

  async function handleStart() {
    if (!selectedRun) return;
    setBusy(true);
    setActionError('');
    setErrorRecord(null);
    try {
      await projectData.update('test_run', selectedRun.id, { state: 'running', updatedAt: new Date().toISOString() });
      setStep(0);
      setPhase('running');
    } catch (err) {
      setActionError((err && err.message) || 'Could not start the task.');
    } finally {
      setBusy(false);
    }
  }

  async function handleStop() {
    if (!selectedRun) return;
    setBusy(true);
    setActionError('');
    try {
      await projectData.update('test_run', selectedRun.id, { state: 'idle', updatedAt: new Date().toISOString() });
      setPhase('idle');
    } catch (err) {
      setActionError((err && err.message) || 'Could not stop the task.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRetry() {
    if (!selectedRun) return;
    setBusy(true);
    setActionError('');
    setErrorRecord(null);
    try {
      await projectData.update('test_run', selectedRun.id, { state: 'running', updatedAt: new Date().toISOString() });
      setStep(0);
      setPhase('running');
    } catch (err) {
      setActionError((err && err.message) || 'Could not retry the task.');
    } finally {
      setBusy(false);
    }
  }

  const persistedErrorRecord = useMemo(() => {
    if (!selectedRun || !Array.isArray(errorLogData)) return null;
    const entries = errorLogData
      .filter((entry) => entry.runId === selectedRun.id)
      .slice()
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    if (!entries.length) return null;
    const latest = entries[0];
    return { message: latest.message, step: latest.failingStep || 'unknown step' };
  }, [errorLogData, selectedRun]);

  const displayedErrorRecord = errorRecord || persistedErrorRecord;

  const phaseForDisplay = loading ? 'loading' : error ? 'error' : !selectedRun ? 'empty' : phase;
  const stateNote = loading
    ? 'Opening the local run store…'
    : phaseForDisplay === 'running'
      ? `Engine processing · ${selectedRun ? selectedRun.configurationSummary : ''}`
      : phaseForDisplay === 'complete'
        ? 'All segments filled · findings ready to review'
        : phaseForDisplay === 'error'
          ? 'Run halted by the engine'
          : phaseForDisplay === 'idle'
            ? 'Ready to start when you are'
            : '';

  return (
    <section className="rtp-root" aria-labelledby="rtp-panel-title">
      <div className="rtp-rail" data-content-rail="workspace">
        <div className="rtp-panel">
          <div className="rtp-panel__head">
            <span className="rtp-panel__kicker" id="rtp-panel-title">
              TASK <strong>{selectedRun ? selectedRun.id : '—'}</strong>
              {selectedRun ? ` · ${selectedRun.configurationSummary}` : ''}
            </span>
            <span className="rtp-chip"><i aria-hidden="true" />Authorized target only</span>
          </div>

          <StateBlock phase={phaseForDisplay} note={stateNote} />

          {phaseForDisplay === 'loading' ? (
            <Skeletons />
          ) : (
            <SteppedProgress step={phaseForDisplay === 'empty' ? 0 : step} running={phaseForDisplay === 'running'} />
          )}

          {phaseForDisplay === 'empty' ? (
            <div className="rtp-empty">
              <h3>No run selected</h3>
              <p>
                Start an authorized test run to monitor it here. Configure a target, wordlist or mask and thread
                count on the New Test Run page, then come back to watch each step.
              </p>
            </div>
          ) : null}

          {phaseForDisplay === 'error' && displayedErrorRecord ? (
            <div className="rtp-error" role="alert">
              <div className="rtp-error__head">
                <h3 className="rtp-error__title">Task failed</h3>
                <span className="rtp-error__tag">Error</span>
              </div>
              <div className="rtp-error__body">
                <p>{displayedErrorRecord.message}</p>
                <p className="rtp-error__step">Failing step: <b>{displayedErrorRecord.step}</b></p>
              </div>
            </div>
          ) : null}

          {phaseForDisplay === 'error' && error && !displayedErrorRecord ? (
            <div className="rtp-error" role="alert">
              <div className="rtp-error__head">
                <h3 className="rtp-error__title">Run store unavailable</h3>
                <span className="rtp-error__tag">Error</span>
              </div>
              <div className="rtp-error__body">
                <p>{error.message || 'The local run store could not be read.'}</p>
                <p className="rtp-error__step">Failing step: <b>load test_run collection</b></p>
              </div>
            </div>
          ) : null}

          {selectedRun && phaseForDisplay !== 'empty' ? (
            <div className="rtp-meta">
              <div className="rtp-meta__row">
                <h3 className="rtp-meta__name">{selectedRun.name}</h3>
                <p className="rtp-meta__target">{selectedRun.target}</p>
              </div>
              <p className="rtp-meta__cfg">{selectedRun.configurationSummary}</p>
              <p className="rtp-meta__stamp">
                Created {stamp(selectedRun.createdAt)}
                {selectedRun.updatedAt ? ` · Updated ${stamp(selectedRun.updatedAt)}` : ''}
              </p>
            </div>
          ) : null}

          <div className="rtp-actions">
            <LastLog note={lastLog} />
            {actionError ? <p className="rtp-note rtp-note--bad" role="alert"><b>!&nbsp;</b>{actionError}</p> : null}

            {phaseForDisplay === 'empty' || phaseForDisplay === 'error' ? (
              <a className="rtp-btn rtp-btn--quiet" href="/new-test-run">New Test Run</a>
            ) : null}

            {phaseForDisplay === 'idle' ? (
              <button type="button" className="rtp-btn rtp-btn--primary" onClick={handleStart} disabled={busy}>
                {busy ? 'Starting…' : 'Start task'}
              </button>
            ) : null}

            {phaseForDisplay === 'running' ? (
              <button type="button" className="rtp-btn rtp-btn--stop" onClick={handleStop} disabled={busy}>
                {busy ? 'Stopping…' : 'Stop task'}
              </button>
            ) : null}

            {phaseForDisplay === 'complete' ? (
              <>
                <a
                  className="rtp-btn rtp-btn--quiet"
                  href={selectedRun ? `/new-test-run?id=${encodeURIComponent(selectedRun.id)}` : '/new-test-run'}
                  title="Configuration preserved"
                >
                  Return to New Test Run
                </a>
                <a
                  className="rtp-btn rtp-btn--primary"
                  href={selectedRun ? `/results?id=${encodeURIComponent(selectedRun.id)}` : '/results'}
                >
                  Open Results
                </a>
              </>
            ) : null}

            {phaseForDisplay === 'error' ? (
              <>
                <a
                  className="rtp-btn rtp-btn--ink"
                  href={selectedRun ? `/new-test-run?id=${encodeURIComponent(selectedRun.id)}` : '/new-test-run'}
                  title="Configuration preserved"
                >
                  Return to New Test Run
                </a>
                <button type="button" className="rtp-btn rtp-btn--primary" onClick={handleRetry} disabled={busy}>
                  {busy ? 'Retrying…' : 'Retry task'}
                </button>
              </>
            ) : null}

            {error && phaseForDisplay === 'error' && !displayedErrorRecord ? (
              <button type="button" className="rtp-btn rtp-btn--ink" onClick={() => refresh()}>
                Reload runs
              </button>
            ) : null}

            {phaseForDisplay === 'loading' ? (
              <button type="button" className="rtp-btn rtp-btn--primary" disabled>Start task</button>
            ) : null}

            {runs.length > 1 ? (
              <span className="rtp-note">
                Run{' '}
                <select
                  aria-label="Select run to monitor"
                  value={selectedRun ? selectedRun.id : ''}
                  onChange={(e) => { runIdRef.current = null; setSelectedRunId(e.target.value); }}
                  style={{ fontFamily: 'inherit', fontSize: '12px', marginLeft: '4px' }}
                >
                  {runs.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function LastLog({ note }) {
  if (!note) return null;
  return <p className="rtp-note" aria-live="off">{note}</p>;
}